import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  MessagesSquare, 
  Send, 
  Loader2,
  Plus,
  MapPin,
  Users,
  Hash
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatsPage = () => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRoomData, setNewRoomData] = useState({ name: '', type: 'topic' });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.room_id);
      // Poll for new messages
      const interval = setInterval(() => fetchMessages(selectedRoom.room_id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API_URL}/chat-rooms`);
      setRooms(response.data.rooms);
      if (response.data.rooms.length > 0 && !selectedRoom) {
        setSelectedRoom(response.data.rooms[0]);
      }
    } catch (error) {
      console.error('Failed to fetch rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const response = await axios.get(`${API_URL}/chat-rooms/${roomId}/messages`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedRoom) return;

    setSending(true);
    try {
      await axios.post(`${API_URL}/chat-rooms/${selectedRoom.room_id}/messages`, {
        text: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedRoom.room_id);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoomData.name.trim()) {
      toast.error('Please enter a room name');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/chat-rooms`, newRoomData);
      setRooms([response.data, ...rooms]);
      setSelectedRoom(response.data);
      setShowCreateDialog(false);
      setNewRoomData({ name: '', type: 'topic' });
      toast.success('Chat room created!');
    } catch (error) {
      toast.error('Failed to create room');
    }
  };

  const getRoomIcon = (type) => {
    switch (type) {
      case 'city':
        return MapPin;
      case 'league':
        return Users;
      default:
        return Hash;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="chats-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-bold text-2xl text-white">Chat Rooms</h1>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="create-room-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Room
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-deep-green border-white/20">
            <DialogHeader>
              <DialogTitle className="text-white font-display">Create Chat Room</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="text-cream/90">Room Name</Label>
                <Input
                  value={newRoomData.name}
                  onChange={(e) => setNewRoomData({ ...newRoomData, name: e.target.value })}
                  placeholder="e.g., Atlanta Golf Network"
                  className="bg-white/10 border-white/20 text-white"
                  data-testid="new-room-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-cream/90">Room Type</Label>
                <select
                  value={newRoomData.type}
                  onChange={(e) => setNewRoomData({ ...newRoomData, type: e.target.value })}
                  className="w-full h-10 px-4 rounded-sm bg-white/10 border border-white/20 text-white"
                >
                  <option value="topic">Topic</option>
                  <option value="city">City</option>
                  <option value="league">League</option>
                </select>
              </div>
              <Button 
                onClick={handleCreateRoom}
                className="w-full btn-gold"
                data-testid="submit-create-room"
              >
                Create Room
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Room List */}
        <Card className="w-72 shrink-0 flex flex-col bg-white/5 border-white/10">
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea className="h-full">
              {rooms.length === 0 ? (
                <div className="p-4 text-center text-cream/60">
                  <MessagesSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No chat rooms</p>
                  <p className="text-xs mt-1">Create one to get started</p>
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {rooms.map((room) => {
                    const RoomIcon = getRoomIcon(room.type);
                    return (
                      <button
                        key={room.room_id}
                        onClick={() => setSelectedRoom(room)}
                        className={cn(
                          "w-full p-3 rounded-sm text-left transition-colors",
                          selectedRoom?.room_id === room.room_id
                            ? "bg-gold text-deep-green"
                            : "hover:bg-white/5 text-white"
                        )}
                        data-testid={`room-${room.room_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-sm flex items-center justify-center",
                            selectedRoom?.room_id === room.room_id
                              ? "bg-deep-green/20"
                              : "bg-gold/20"
                          )}>
                            <RoomIcon className={cn(
                              "w-5 h-5",
                              selectedRoom?.room_id === room.room_id
                                ? "text-deep-green"
                                : "text-gold"
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">{room.name}</p>
                            <p className={cn(
                              "text-xs capitalize",
                              selectedRoom?.room_id === room.room_id
                                ? "text-deep-green/70"
                                : "text-white/50"
                            )}>
                              {room.type} room
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col bg-white/5 border-white/10">
          {selectedRoom ? (
            <>
              {/* Chat Header */}
              <CardHeader className="border-b border-white/10 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-gold/20 flex items-center justify-center">
                      {(() => {
                        const Icon = getRoomIcon(selectedRoom.type);
                        return <Icon className="w-5 h-5 text-gold" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{selectedRoom.name}</p>
                      <p className="text-xs text-cream/50 capitalize">{selectedRoom.type} room</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-white/30 text-cream/70">
                    <Users className="w-3 h-3 mr-1" />
                    {selectedRoom.moderators?.length || 0} mods
                  </Badge>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-4 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-12 text-cream/50">
                        <MessagesSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isOwn = msg.sender_uid === user?.uid;
                        return (
                          <div
                            key={msg.message_id}
                            className={cn("flex gap-3", isOwn && "flex-row-reverse")}
                          >
                            <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
                              <span className="text-gold text-sm font-semibold">
                                {msg.sender_name?.charAt(0) || 'U'}
                              </span>
                            </div>
                            <div className={cn("max-w-[70%]", isOwn && "text-right")}>
                              <p className={cn("text-xs text-cream/50 mb-1", isOwn && "text-right")}>
                                {msg.sender_name} · {format(new Date(msg.created_at), 'h:mm a')}
                              </p>
                              <div
                                className={cn(
                                  "rounded-sm px-4 py-2 inline-block",
                                  isOwn
                                    ? "bg-gold text-deep-green"
                                    : "bg-white/10 text-white"
                                )}
                              >
                                <p>{msg.text}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <form onSubmit={handleSend} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={`Message #${selectedRoom.name}`}
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="chat-message-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-gold"
                    disabled={sending || !newMessage.trim()}
                    data-testid="send-chat-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-cream/60">
              <div className="text-center">
                <MessagesSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a room to join the conversation</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChatsPage;
